import React, { useEffect, useMemo, useState } from "react";
import "./styles/PhishingManager.module.scss";
import CampaignSetup from "./CampaignSetup";
import { MenuProps, Select, Spin } from "antd";
import { Button, Fx, Icon, Pagination } from "Components";
import { GetGroupsListRequest, GetGroupsListResponse } from "Services/Api/Groups/Types";
import { DEFAULT_TABLE_REQUEST_VALUES } from "Pages/Groups/Group/Group";
import { useLazyQuery } from "@apollo/client";
import { GET_GROUPS_LIST } from "Services/Api/Groups/Queries";
import { authStore } from "Stores/AuthStore";
import { SystemRoleType } from "Services/Api/Roles/Types";
import { getPhishingCampaignData, getPhishingCampaignsForTenant } from "Services/Api/PhishingManager/PhishingManager.Service";
import CampaignLimitWarningModal from "./Phishing/CampaignLimitWarningModal";
import DiscardCampaignModal from "./Phishing/DiscardCampaignModal";
import CampaignList from "./Phishing/CampaignList";
import EmptyState from "./Phishing/EmptyState";
import CampaignLimitToast from "./Phishing/CampaignLimitToast";
import { DEFAULT_CAMPAIGNS_PAGESIZE, DEFAULT_WHITELIST_URL } from "./PhishingManager.constants";
import Header from "./components/Header";
import Alert from "./components/Alert";
import { useIsMobile } from "Hooks";
import InfinityScrollWrapper from "Components/InfinityScroll/InfinityScrollWrapper";



export interface CampaignData {
  id: number | string;
  name: string;
  status: string;
  campaign_type: string;
  schedule_datetime: string | null;
  schedule_timezone: string | null;
  campaign_duration: number;
  image_url: string;
}

export interface CampaignThresholdData {
  campaign_count: number;
  first_campaign_created_at: string;
  tenant_campaigns_threshold: number;
  current_period_started_at: string;
  threshold_period_months: number;
  is_threshold_reached: boolean;
}
type MenuClickInfo = Parameters<NonNullable<MenuProps["onClick"]>>[0];

const PhishingManager: React.FC = () => {
  const [campaignThresholdData, setCampaignThresholdData] = useState<CampaignThresholdData | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [selectedTenantId, setSelectedTenantId] = useState<string | number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [phishingArray, setPhishingArray] = useState<CampaignData[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [itemToDiscard, setItemToDiscard] = useState<string | null>(null);
  const [warningModal, setWarningModal] = useState(false);
  const [newCampaignSetup, setNewCampaignSetup] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_CAMPAIGNS_PAGESIZE);
  const threshold = campaignThresholdData?.tenant_campaigns_threshold ?? 0;
  const isMobile = useIsMobile();

  const handleMenuClick =
    (itemId: string) =>
      (info: MenuClickInfo) => {
        if (info.key === "discard") {
          setItemToDiscard(itemId);
          setIsModalVisible(true);
        }
      };

  const [getGroup, result] = useLazyQuery<
    GetGroupsListResponse,
    GetGroupsListRequest
  >(GET_GROUPS_LIST, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  const TENANTS: { label: string; value: string }[] = useMemo(() => {
    return (
      result?.data?.listGroups?.map((el) => ({
        label: el.name,
        value: String(el.id),
      })) ?? []
    );
  }, [result?.data?.listGroups]);

  const confirmDiscard = () => {
    if (itemToDiscard) {
      setPhishingArray(prev => prev.filter(el => el.id !== Number(itemToDiscard)));
    }
    setIsModalVisible(false);
    setItemToDiscard(null);
  };

  const confirmWarningDiscard = () => {
    newCampaign();
    setWarningModal(false);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setItemToDiscard(null);
  };

  const closeWarningModal = () => {
    setWarningModal(false);
  };

  const handleCreateCampaign = () => {
    if (phishingArray.length === (threshold - 1)) {
      setWarningModal(true);
    } else if (!!campaignThresholdData?.is_threshold_reached && phishingArray.length >= threshold) {
      setErrorMessage("Campaign limit reached. No more campaigns can be created.");
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
      }, 3000);
    } else {
      if (!!campaignThresholdData) {
        newCampaign();
      } else {
        if (!!errorMessage) {
          setErrorMessage(errorMessage);
          setShowToast(true);
          setTimeout(() => {
            setShowToast(false);
          }, 3000)
        }
      }
    }
  };

  const newCampaign = () => {
    setNewCampaignSetup(true);
  }

  const isSuperAdmin =
    authStore.currentUser?.primarySystemRole?.role ===
    SystemRoleType.SuperAdmin;


  const handleTenantChange = async (id: string | number) => {
    setIsLoading(true);
    setCurrentPage(1);
    await fetchCampaigns(id);
    await fetchCampaignsData(String(id));
    setSelectedTenantId(id);
  }

  const fetchCampaigns = async (value: string | number | undefined, pagination?: number) => {
    try {
      setIsLoading(true);
      const page = pagination ?? 1
      const response = await getPhishingCampaignsForTenant(value, page);
      const { current_page, per_page, total } = response?.meta || {};
      if (response?.meta) {
        setTotalItems(total);
        setPageSize(per_page);
        setCurrentPage(current_page);
      }
      const campaignData = response?.data.map((el: CampaignData) => {
        let formattedStatus = "";
        switch (el.status) {
          case "draft": formattedStatus = "Draft"; break;
          case "in_testing": formattedStatus = "Testing"; break;
          case "completed": formattedStatus = "Complete"; break;
          case "scheduled": formattedStatus = "Scheduled"; break;
          case "in_progress": formattedStatus = "Live"; break;
          case "cancelled": formattedStatus = "Cancelled"; break;
          case "failed": formattedStatus = "Failed"; break;
          default: formattedStatus = el.status;
        }
        return { ...el, status: formattedStatus };
      }) || [];
      setPhishingArray(prev => {
        if (!isMobile || current_page === 1) return campaignData;
        return [...prev, ...campaignData];
      });
    } catch (error) {
      // setPhishingArray([]);
      console.error("Failed to fetch campaigns:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCampaignsData = async (value: string) => {
    setIsLoading(true);
    try {
      const response = await getPhishingCampaignData(value);
      setCampaignThresholdData(response.data);
      if (response.data?.is_threshold_reached) {
        setErrorMessage("Campaign limit reached. No more campaigns can be created.");
        setShowToast(true);
        setTimeout(() => {
          setShowToast(false);
        }, 3000);
      } else {
        setErrorMessage("");
      }
    } catch (error: any) {
      setCampaignThresholdData(null);
      const errorMessage =
        error?.data?.errors[0] ||
        error?.data?.message ||
        "";
      if (!!errorMessage) {
        setErrorMessage(errorMessage);
        setShowToast(true);
        setTimeout(() => {
          setShowToast(false);
        }, 3000)
      }
      console.error("Failed to fetch campaigns:", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCampaignSetupBack = () => {
    setNewCampaignSetup(false);
    fetchCampaigns(selectedTenantId);
  };

  useEffect(() => {
    if (!!errorMessage) {
      setErrorMessage(errorMessage);
      setShowToast(true);
      setTimeout(() => {
        setShowToast(false);
        setErrorMessage("");
      }, 3000)
    }
  }, [errorMessage]);

  useEffect(() => {
    const fetchTenants = (
      values: GetGroupsListRequest = DEFAULT_TABLE_REQUEST_VALUES
    ) => {
      setIsLoading(true);

      const variables: GetGroupsListRequest = {
        sort: values.sort,
        filters: {
          ...values.filters,
          depth: 1,
          parentId: null
        }
      };

      setIsLoading(false);

      if (result.refetch) {
        result.refetch(variables);
      } else {
        getGroup({ variables });
      }
    };

    fetchTenants();
  }, []);

  useEffect(() => {
    if (!isSuperAdmin && TENANTS.length > 0 && !selectedTenantId) {
      setSelectedTenantId(TENANTS[0].value);
      handleTenantChange(TENANTS[0].value);
    }
  }, [TENANTS, isSuperAdmin, selectedTenantId]);

  return (
    <Spin spinning={isLoading}>
      <div className="phishingManager">
        <CampaignLimitToast
          visible={showToast}
          message={errorMessage ?? ""}
          onClose={() => setShowToast(false)}
        />
        {newCampaignSetup ?
          <CampaignSetup {...{ isMobile, errorMessage, setErrorMessage }} thresholdData={campaignThresholdData} tenantId={selectedTenantId} onBack={handleCampaignSetupBack} /> :
          <>
            <Header title="Phishing Manager" description="Create, monitor, and learn from phishing simulations">
              <div className="pm-dropdown-section">
                <label htmlFor="pm-client-select" className="pm-client-label">Tenant</label>
                <Select
                  id="pm-client-select"
                  placeholder="Select a Tenant"
                  dropdownClassName="campaign-setup-filter-option-select"
                  value={selectedTenantId}
                  loading={isLoading}
                  onChange={(value) => handleTenantChange(value)}
                  showSearch
                  disabled={!isSuperAdmin}
                  suffixIcon={<Icon type="campaignDropdown" />}
                  options={TENANTS}
                  getPopupContainer={(trigger) => trigger.parentElement}
                />
              </div>

              <Button shape="round" type="primary" className={`cta-prominent ${(totalItems >= threshold) ? (!selectedTenantId || !!errorMessage) ? "cta-disabled" : "cta-disabled" : ""}`} onClick={handleCreateCampaign} disabled={(totalItems >= threshold) ? (!selectedTenantId || !!errorMessage) ? true : true : false}>
                <Icon type="campaignAdd" />
                New Campaign
              </Button>
            </Header>

            {
              phishingArray.length === 0 ? !selectedTenantId ?
                <EmptyState
                  title="No tenants selected yet"
                  description=" You haven’t selected any tenants campaigns yet. Start by selecting a tenant
                  to create a phishing campaign."
                  actionLabel={null}
                />
                : (
                  <EmptyState
                    title="No campaigns yet"
                    description=" You haven’t created any phishing campaigns yet. Start by creating a campaign
                        to simulate phishing attacks and measure how your users respond."
                    onAction={handleCreateCampaign}
                  />
                ) : (
                <div className="pm-campaign-container">
                  <div className="pm-campaigns-header">Campaigns</div>
                  {isMobile ? (
                    <InfinityScrollWrapper
                      dataLength={phishingArray.length}
                      hasMore={phishingArray.length < totalItems}
                      onLoadMore={() => fetchCampaigns(selectedTenantId, currentPage + 1)}
                      height={1000}
                      scrollableId="campaign-scroll-container"
                      loaderText={"Loading more campaigns..."}
                      endMessageText={"All campaigns loaded"}
                    >
                      <CampaignList campaigns={phishingArray} onMenuClick={handleMenuClick} />
                    </InfinityScrollWrapper>
                  ) :
                    <CampaignList campaigns={phishingArray} onMenuClick={handleMenuClick} />}

                  {(!isMobile && (totalItems / pageSize) > 1) && <Fx shrink={0} fullWidth justify="center">
                    <Pagination
                      total={totalItems}
                      pageSize={pageSize}
                      current={currentPage}
                      onChange={(page) => {
                        setCurrentPage(page);
                        fetchCampaigns(selectedTenantId, page);
                      }}
                      showSizeChanger={false}
                      hideOnSinglePage={true}
                      responsive={true}
                    />
                  </Fx>}
                </div>
              )
            }
            <CampaignLimitWarningModal
              open={warningModal}
              onClose={closeWarningModal}
              onConfirm={confirmWarningDiscard}
            />
            <DiscardCampaignModal
              open={isModalVisible}
              onClose={closeModal}
              onConfirm={confirmDiscard}
            />
            <Alert
              title="Configure Whitelisting for Successful Campaigns"
              subtitle="To ensure phishing simulations are delivered, clients must whitelist
              Venza’s email infrastructure. This step allows campaigns to be
              accessible and prevents client-side security controls from blocking
              email delivery."
              btnText="View Whitelisting Guide"
              btnClick={() => window.open(DEFAULT_WHITELIST_URL, "_blank")}
            />
          </>
        }
      </div >
    </Spin>
  );
};

export default PhishingManager;